import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Route, Routes, useParams, useNavigate } from "react-router-dom";
import "../../styles/tendering.scss";
import "../../styles/tenderingPage.scss";
import Create from "./Create";
import useTendering from "../../hooks/useTendering";
import LandingPage from "./LandingPage";
import Participate from "./Participate";
import Quotation from "./Quotation";
import Detail from "./Detail";
import Contract from "./Contract";
import Documents from "./Documents";
import TenderingDetailContext from "./context";
import AppContext from "../../AppContext";
import { endOfDay } from "date-fns";
import { SocketContext } from "../../contexts/SocketContext";
import PaymentPrompt from "../../components/Tendering/PaymentPrompt";
import Payment from "../../components/Tendering/Payment";
import useQuotation from "../../hooks/useQuotation";
import { CART_STEP, PAYMENT_METHOD } from "../../contexts/CartContext";
import useJournals from "../../hooks/useJournals";

const TenderingDetail = () => {
  const { id } = useParams();
  const { user } = useContext(AppContext);
  const { socket } = useContext(SocketContext);
  const { getById } = useTendering();
  const { getQuotation, createJournal } = useQuotation();
  const { chargeJournal, deleteChargeJournal, getChargeJournal } =
    useJournals();
  const [details, setDetails] = useState(undefined);
  const [currentQuotation, setCurrentQuotation] = useState(undefined);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [projectState, setProjectState] = useState(undefined);
  const [isTogglePaymentConfirmModal, setIsTogglePaymentConfirmModal] =
    useState(false);
  const [isTogglePaymentModal, setIsTogglePaymentModal] = useState(false);
  const navigate = useNavigate();

  // * Payment related
  const [journal, setJournal] = useState(undefined);
  const [paymentMethod, setPaymentMethod] = useState(undefined);
  const [paymentStep, setPaymentStep] = useState(CART_STEP.INFO);
  const [checkingOut, setCheckingOut] = useState(false);
  const [cardDetails, setCardDetails] = useState(undefined);
  const [charge, setCharge] = useState(undefined);
  const [charging, setCharging] = useState(false);
  const [chargeExpiresIn, setChargeExpiresIn] = useState(undefined);
  const countdownIntervalRef = useRef(null);
  const fetchChargeIntervalRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (!socket) return;

    socket.on("tenderings:project-updated", (payload) => {
      if (!payload) return;

      const { _id } = payload;
      if (!_id) return;

      if (_id !== id) return;

      fetchData();
    });

    return () => {
      socket.removeAllListeners("tenderings:project-updated");
    };
  }, [socket]);

  useEffect(() => {
    if (paymentStep === CART_STEP.INFO) {
      setPaymentMethod(undefined);
    }

    // * Always scroll to top when step changed (Mobile support)
    const container = document.querySelector(".overlay");

    if (!container) return;

    container.scrollTop = 0;
  }, [paymentStep]);

  const _clearInvtervals = () => {
    setChargeExpiresIn(undefined);

    if (fetchChargeIntervalRef.current)
      clearInterval(fetchChargeIntervalRef.current);
    if (countdownIntervalRef.current)
      clearInterval(countdownIntervalRef.current);
  };

  /**
   * Create or clear intervals for fetching charges for counting down time left
   */
  useEffect(() => {
    if (!charge) {
      _clearInvtervals();
      return;
    }

    // * Skip creating invterval if already exists
    if (charge && chargeExpiresIn) return;

    // * Skip if payment is card by checking expiresOn
    if (!charge.expiresOn) return;

    setChargeExpiresIn(
      (new Date(charge.expiresOn).getTime() - new Date().getTime()) / 100
    );

    // * Set interval to fetch charge status
    fetchChargeIntervalRef.current = setInterval(() => {
      getChargeJournal(journal._id).then(({ data: { status } }) => {
        if (status === "successfull") {
          _clearInvtervals();
          fetchData();
          return;
        }

        setCharge((curr) => ({
          ...curr,
          status,
        }));
      });
    }, 1000);

    // * Set interval to coutdown timer
    countdownIntervalRef.current = setInterval(() => {
      setChargeExpiresIn((time) => {
        if (time <= 0) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }

        return time - 1;
      });
    }, 1000);

    return () => {};
  }, [charge]);

  const fetchData = () => {
    getById(id).then(async (response) => {
      setDetails(response);

      const { period, createdBy, participants } = response;

      const _isOwner = createdBy === user?._id;
      const _isOpen =
        new Date() > new Date(period.start) &&
        new Date() < endOfDay(new Date(period.end));
      const _canEdit = _isOwner && !_isOpen;

      const _participation = participants?.find((p) => p.user === user?._id);
      const _selectedForTendering = _participation?.accepted === true;

      const _isContractor = _participation?.won ?? false;

      // * Get quotation status
      if (response.status === 4) {
        const _quotationId = response.contractor.quotations.at(-1);

        getQuotation(_quotationId)
          .then((data) => {
            setCurrentQuotation(data);
            setPaymentTerms([data.advancedPayment, ...data.paymentTerms]);
          })
          .catch((err) => console.error(err));
      }

      setProjectState({
        isOwner: _isOwner,
        isOpen: _isOpen,
        canEdit: _canEdit,
        participation: _participation,
        selectedForTendering: _selectedForTendering,
        isContractor: _isContractor,
      });
    });
  };

  const handleTogglePayment = () => {
    setIsTogglePaymentConfirmModal(true);
  };

  const handleCheckout = async () => {
    setCheckingOut(true);

    try {
      // * Create journal
      const { data: journal } = await createJournal(currentQuotation._id);

      setJournal(journal);

      if (journal.status !== 2) {
        setPaymentMethod(PAYMENT_METHOD.CARD);
        setPaymentStep(CART_STEP.PAYMENT);
        return;
      }
    } catch (error) {
      console.error(error);
    } finally {
      setCheckingOut(false);
    }
  };

  const handleCardDetails = (cardNo, name, expiration, cvv) => {
    setCardDetails({
      cardNo,
      name,
      expiration,
      cvv,
    });
  };

  const handleSetPaymentMethod = async (_paymentMethod) => {
    if (
      paymentMethod === PAYMENT_METHOD.QR ||
      paymentMethod === PAYMENT_METHOD.TRUEMONEY
    ) {
      await expireCharge();
    }

    setCharge(undefined);

    setPaymentMethod(_paymentMethod);

    // * Auto charge if not CARD
    if (_paymentMethod === PAYMENT_METHOD.CARD || !_paymentMethod) return;

    handleChargeJournal(_paymentMethod);
  };

  const handleChargeJournal = async (paymentMethod) => {
    setCharging(true);

    if (paymentMethod === PAYMENT_METHOD.CARD) {
      return new Promise((resolve, reject) => {
        const { cardNo, name, expiration, cvv } = cardDetails;

        const [expirationMonth, expirationYear] = expiration.split("/");

        const tokenParameters = {
          expiration_month: expirationMonth,
          expiration_year: expirationYear,
          number: cardNo,
          security_code: cvv,
          name: name,
        };

        Omise.createToken(
          "card",
          tokenParameters,
          async (statusCode, response) => {
            if (!response || !response.card) {
              console.error("Error calling omise");
              toast.error(`${response.message}`);
              reject(response.message);
              return;
            }

            const cardToken = response.id;

            const { data: _charge } = await chargeJournal(journal._id, {
              method: "card",
              token: cardToken,
            });

            window.open(_charge.authorizeUri, "_self");
            return;

            // setCharge(_charge);
            // setCharging(false);
            // resolve(_charge);
          }
        );
      });
    }

    const { data: _charge } = await chargeJournal(journal._id, {
      method: paymentMethod === PAYMENT_METHOD.QR ? "promptpay" : "truemoney",
    });

    setCharge(_charge);
    setCharging(false);
    return _charge;
  };

  const expireCharge = async () => {
    // * Delete charge if used to have
    if (!journal) {
      return;
    }

    if (!paymentMethod || paymentMethod === PAYMENT_METHOD.CARD) return;

    if (charge && charge.status === "successfull") return;

    try {
      setCharge(undefined);
      await deleteChargeJournal(journal._id);
    } catch (error) {}
  };

  const handleRestartPayment = async () => {
    setPaymentStep(CART_STEP.INFO);
    setPaymentMethod(undefined);
    await expireCharge();
    setJournal(undefined);
    setCharge(undefined);
    setCardDetails(undefined);
    setCheckingOut(false);
    setCharging(false);
    setChargeExpiresIn(undefined);
  };

  const contextValues = useMemo(
    () => ({
      details,
      setDetails,
      projectState,
      setProjectState,
      refresh: fetchData,
      togglePayment: handleTogglePayment,
      currentQuotation,
      paymentTerms,
      paymentStep,
      setPaymentStep,
      paymentMethod,
      setPaymentMethod: handleSetPaymentMethod,
      checkout: handleCheckout,
      journal,
      checkingOut,
      restartPayment: handleRestartPayment,
      cardDetails,
      setCardDetails: handleCardDetails,
      charge,
      setCharge,
      charging,
      setCharging,
      chargeExpiresIn,
      setChargeExpiresIn,
      chargeJournal: handleChargeJournal,
    }),
    [
      details,
      projectState,
      paymentTerms,
      currentQuotation,
      paymentStep,
      paymentMethod,
      journal,
      checkingOut,
      cardDetails,
      charge,
      charging,
      chargeExpiresIn,
    ]
  );

  if (!details) return <></>;

  return (
    <TenderingDetailContext.Provider value={contextValues}>
      <Routes>
        <Route path="" element={<Detail />} />
        <Route path="participate" element={<Participate />} />
        <Route path="quotation/*" element={<Quotation />} />
        <Route path="contract/*" element={<Contract />} />
        <Route path="documents/*" element={<Documents />} />
      </Routes>
      {isTogglePaymentConfirmModal && (
        <PaymentPrompt
          onClose={() => setIsTogglePaymentConfirmModal(undefined)}
          onConfirm={() => {
            setIsTogglePaymentModal(isTogglePaymentConfirmModal);
            setIsTogglePaymentConfirmModal(undefined);
          }}
        />
      )}
      {isTogglePaymentModal && (
        <Payment onClose={() => setIsTogglePaymentModal(false)} />
      )}
    </TenderingDetailContext.Provider>
  );
};

function tenderingPage(props) {
  return (
    <Routes>
      <Route path="" element={<LandingPage />} />
      <Route path="create/*" element={<Create />} />
      <Route path=":id/*" element={<TenderingDetail />} />
      <Route path=":tenderingId/edit/*" element={<Create />} />
    </Routes>
  );
}

tenderingPage.propTypes = {};

export default tenderingPage;
